import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/config';
import { useAuth } from '@/contexts/AuthContext';
import { useFreeTrial } from '@/hooks/useFreeTrial';
import { useEvaluation } from '@/contexts/EvaluationContext';
import { usePremiumCredits } from '@/hooks/usePremiumCredits';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Share2, Sparkles, Loader2, ArrowLeft } from 'lucide-react';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { detectUserCurrency, CURRENCY_SYMBOLS, PRICE_AMOUNTS } from '@/lib/stripe-prices';
import PremiumInsights from '@/components/PremiumInsights';
import { PremiumCreditsDisplay } from '@/components/PremiumCreditsDisplay';

const Result = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { markFreeTrialAsUsed } = useFreeTrial();
  const { endFreeTrial } = useEvaluation();
  const { hasPremium, creditsRemaining, loading: creditsLoading, refresh: refreshCredits } = usePremiumCredits();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const [score, setScore] = useState<number | null>(null);
  const [description, setDescription] = useState<string>('');
  const [editedImage, setEditedImage] = useState<string | null>(null);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [evaluationId, setEvaluationId] = useState<string | null>(null);
  const [currency] = useState(detectUserCurrency());
  const [premiumInsights, setPremiumInsights] = useState<any>(null);
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [errorState, setErrorState] = useState<string | null>(null);
  const [pendingImageCreation, setPendingImageCreation] = useState(false);
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  
  const imageFromState = location.state?.image;
  const evaluationIdFromUrl = searchParams.get('evaluation');
  const freeTrialModeFromState = location.state?.freeTrialMode;
  const hasStartedEvaluation = useRef(false);

  useEffect(() => {
    // Wait for credits to load before evaluating
    if (creditsLoading) {
      console.log('⏳ Waiting for premium credits to load...');
      return;
    }

    // Check sessionStorage for pending evaluation
    const savedImage = sessionStorage.getItem('pendingEvaluation');
    const savedFreeTrial = sessionStorage.getItem('pendingFreeTrialMode') === 'true';
    
    if (evaluationIdFromUrl) {
      loadExistingEvaluation();
    } else if ((imageFromState || savedImage) && !hasStartedEvaluation.current) {
      hasStartedEvaluation.current = true;
      
      const imageToUse = imageFromState || savedImage;
      const freeTrialToUse = freeTrialModeFromState || savedFreeTrial;
      
      if (!user && !freeTrialToUse) {
        toast.error(t('result.loginError'));
        navigate('/auth');
        return;
      }
      
      if (freeTrialToUse) {
        setIsFreeTrial(true);
      }

      // Store in sessionStorage to prevent loss on refresh
      if (imageToUse) {
        sessionStorage.setItem('pendingEvaluation', imageToUse);
        sessionStorage.setItem('pendingFreeTrialMode', String(freeTrialToUse));
      }

      if (imageToUse) {
        setOriginalImage(imageToUse);
        evaluateTree(imageToUse);
      }
    } else if (!imageFromState && !savedImage && !evaluationIdFromUrl && !hasStartedEvaluation.current) {
      navigate('/');
    }
  }, [user, evaluationIdFromUrl, imageFromState, freeTrialModeFromState, creditsLoading]);

  // Effect to create image when loading existing evaluations
  useEffect(() => {
    // Only for loading existing evaluations (via URL)
    if (score && originalImage && canvasRef.current && !editedImage && !imageProcessing && evaluationId) {
      console.log('🎨 Creating image from existing evaluation...');
      createEditedImage(score, originalImage);
    }
  }, [score, originalImage, editedImage, imageProcessing, evaluationId]);

  // Monitor canvas availability using state
  useEffect(() => {
    const checkCanvas = () => {
      const ready = !!canvasRef.current;
      if (ready !== isCanvasReady) {
        console.log('🎯 Canvas availability changed:', ready);
        setIsCanvasReady(ready);
      }
    };
    
    checkCanvas();
    const timer = setInterval(checkCanvas, 100);
    
    return () => clearInterval(timer);
  }, [score, loading, isCanvasReady]);

  // Create image when all conditions are met
  useEffect(() => {
    console.log('🔍 Image creation check:', {
      pending: pendingImageCreation,
      hasScore: score !== null,
      hasOriginalImage: !!originalImage,
      canvasReady: isCanvasReady,
      hasEditedImage: !!editedImage,
      isProcessing: imageProcessing,
    });

    if (pendingImageCreation && score !== null && originalImage && isCanvasReady && !editedImage && !imageProcessing) {
      console.log('🎨 Creating image - all conditions met');
      createEditedImage(score, originalImage);
      setPendingImageCreation(false);
    }
  }, [pendingImageCreation, score, originalImage, isCanvasReady, editedImage, imageProcessing]);

  const loadExistingEvaluation = async () => {
    try {
      setLoading(true);
      
      const { data: evaluation, error } = await supabase
        .from('evaluations')
        .select('*')
        .eq('id', evaluationIdFromUrl)
        .eq('user_id', user?.id)
        .single();

      if (error) throw error;

      setScore(evaluation.score);
      setDescription(evaluation.description);
      setOriginalImage(evaluation.image_data);
      setEvaluationId(evaluation.id);
      
      if (evaluation.insights) {
        setPremiumInsights(evaluation.insights);
      }
    } catch (error) {
      console.error('Error loading evaluation:', error);
      toast.error(t('result.evaluationError'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const evaluateTree = async (imageToEvaluate?: string) => {
    try {
      setLoading(true);
      setErrorState(null);
      const imageData = imageToEvaluate || imageFromState;
      
      if (!imageData) {
        throw new Error('No image data available');
      }
      
      setOriginalImage(imageData);
      
      // Use premium if user has credits
      const usePremium = hasPremium && !isFreeTrial;
      
      console.log('🎄 Evaluating tree...', { 
        hasPremium, 
        creditsRemaining, 
        creditsLoading, 
        isFreeTrial,
        usePremium 
      });
      
      const { data, error } = await supabase.functions.invoke('evaluate-tree', {
        body: { 
          image: imageData, 
          isPremium: usePremium,
          language: i18n.language,
        },
      });

      if (error) throw error;

      console.log('✅ Evaluation complete, setting score:', data.score);
      setScore(data.score);
      setPendingImageCreation(true);
      console.log('✅ Score state updated, image creation pending');
      
      if (isFreeTrial) {
        // Free trial: only show score, don't save to database
        await markFreeTrialAsUsed();
        sessionStorage.removeItem('pendingEvaluation');
        sessionStorage.removeItem('pendingFreeTrialMode');
      } else {
        // Logged in user: show description and save to database
        setDescription(data.description);
        
        // If premium insights were returned, set them
        if (data.categories && data.trends && data.suggestions) {
          setPremiumInsights(data);
        }
        
        const { data: evalData, error: evalError } = await supabase
          .from('evaluations')
          .insert({
            user_id: user?.id,
            score: Math.round(data.score), // Convert to integer for database
            description: data.description,
            image_data: imageData,
            insights: (data.categories && data.trends && data.suggestions) ? data : null,
          })
          .select()
          .single();
        
        if (evalError) {
          console.error('Error saving evaluation:', evalError);
          toast.error('Failed to save evaluation');
        } else {
          setEvaluationId(evalData.id);
          sessionStorage.removeItem('pendingEvaluation');
          sessionStorage.removeItem('pendingFreeTrialMode');
          
          // Refresh credits after evaluation
          if (usePremium) {
            await refreshCredits();
          }
        }
      }
      
      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#2d7a4f', '#dc2626', '#eab308'],
      });
    } catch (error) {
      console.error('Evaluation error:', error);
      setErrorState(t('result.evaluationError'));
      // Don't navigate away - show error instead
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for Christmas decorations
  const drawStar = (ctx: CanvasRenderingContext2D, cx: number, cy: number, spikes: number, outerRadius: number, innerRadius: number, color: string) => {
    let rot = Math.PI / 2 * 3;
    let x = cx;
    let y = cy;
    const step = Math.PI / spikes;

    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);
    for (let i = 0; i < spikes; i++) {
      x = cx + Math.cos(rot) * outerRadius;
      y = cy + Math.sin(rot) * outerRadius;
      ctx.lineTo(x, y);
      rot += step;

      x = cx + Math.cos(rot) * innerRadius;
      y = cy + Math.sin(rot) * innerRadius;
      ctx.lineTo(x, y);
      rot += step;
    }
    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  };

  const drawSnowflake = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, color: string) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      const endX = x + Math.cos(angle) * size;
      const endY = y + Math.sin(angle) * size;
      
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(endX, endY);
      ctx.stroke();
      
      // Add smaller branches
      const branchSize = size * 0.4;
      const branchX = x + Math.cos(angle) * (size * 0.6);
      const branchY = y + Math.sin(angle) * (size * 0.6);
      
      for (let j = -1; j <= 1; j += 2) {
        const branchAngle = angle + (Math.PI / 6) * j;
        const branchEndX = branchX + Math.cos(branchAngle) * branchSize;
        const branchEndY = branchY + Math.sin(branchAngle) * branchSize;
        ctx.beginPath();
        ctx.moveTo(branchX, branchY);
        ctx.lineTo(branchEndX, branchEndY);
        ctx.stroke();
      }
    }
  };

  const drawOrnament = (ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string) => {
    // Ball
    const gradient = ctx.createRadialGradient(x - radius/3, y - radius/3, 0, x, y, radius);
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, color.replace('1)', '0.6)'));
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    
    // Highlight
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.beginPath();
    ctx.arc(x - radius/3, y - radius/3, radius/3, 0, Math.PI * 2);
    ctx.fill();
    
    // Top cap
    ctx.fillStyle = '#d4af37';
    ctx.fillRect(x - radius/4, y - radius - 8, radius/2, 8);
  };

  const drawChristmasLights = (ctx: CanvasRenderingContext2D, startX: number, startY: number, endX: number, endY: number, count: number) => {
    const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'];
    const dx = (endX - startX) / count;
    const dy = (endY - startY) / count;
    
    // Wire
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();
    
    // Lights
    for (let i = 0; i < count; i++) {
      const x = startX + dx * i;
      const y = startY + dy * i;
      const color = colors[i % colors.length];
      
      // Glow
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, 12);
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, 12, 0, Math.PI * 2);
      ctx.fill();
      
      // Bulb
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const drawPineBranch = (ctx: CanvasRenderingContext2D, x: number, y: number, length: number, angle: number) => {
    ctx.strokeStyle = '#2d7a4f';
    ctx.lineWidth = 4;
    
    const endX = x + Math.cos(angle) * length;
    const endY = y + Math.sin(angle) * length;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(endX, endY);
    ctx.stroke();
    
    // Needles
    ctx.lineWidth = 2;
    for (let i = 0; i < length; i += 15) {
      const px = x + Math.cos(angle) * i;
      const py = y + Math.sin(angle) * i;
      
      for (let side = -1; side <= 1; side += 2) {
        const needleAngle = angle + (Math.PI / 3) * side;
        const needleEndX = px + Math.cos(needleAngle) * 20;
        const needleEndY = py + Math.sin(needleAngle) * 20;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(needleEndX, needleEndY);
        ctx.stroke();
      }
    }
  };

  const drawBow = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
    ctx.fillStyle = '#dc2626';
    
    // Left loop
    ctx.beginPath();
    ctx.ellipse(x - size/2, y, size/2, size, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Right loop
    ctx.beginPath();
    ctx.ellipse(x + size/2, y, size/2, size, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Center knot
    ctx.beginPath();
    ctx.arc(x, y, size/3, 0, Math.PI * 2);
    ctx.fill();
    
    // Ribbons
    ctx.beginPath();
    ctx.moveTo(x - size/4, y + size/2);
    ctx.lineTo(x - size/2, y + size * 1.5);
    ctx.lineTo(x, y + size);
    ctx.closePath();
    ctx.fill();
    
    ctx.beginPath();
    ctx.moveTo(x + size/4, y + size/2);
    ctx.lineTo(x + size/2, y + size * 1.5);
    ctx.lineTo(x, y + size);
    ctx.closePath();
    ctx.fill();
  };

  const drawCandyCane = (ctx: CanvasRenderingContext2D, x: number, y: number, height: number) => {
    const stripeWidth = 8;
    
    // White base
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 20;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(x, y, 25, Math.PI, 0, false);
    ctx.lineTo(x + 25, y + height);
    ctx.stroke();
    
    // Red stripes
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = 8;
    
    for (let i = 0; i < height; i += stripeWidth * 2) {
      ctx.beginPath();
      ctx.moveTo(x + 25, y + i);
      ctx.lineTo(x + 25, y + i + stripeWidth);
      ctx.stroke();
    }
  };

  const createEditedImage = (treeScore: number, imgSrc: string) => {
    const currentLanguage = i18n.language;
    console.log('=== createEditedImage called ===');
    console.log('📊 Score:', treeScore);
    console.log('🌐 Language:', currentLanguage);
    console.log('🖼️ Image source length:', imgSrc?.length);
    console.log('🎨 Canvas ref exists:', !!canvasRef.current);

    if (!canvasRef.current) {
      console.error('❌ Canvas ref is null!');
      setImageError('Canvas não disponível');
      return;
    }

    setImageProcessing(true);
    setImageError(null);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('❌ Failed to get 2d context');
      setImageError('Erro ao criar contexto do canvas');
      setImageProcessing(false);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onerror = (e) => {
      console.error('❌ Image load error:', e);
      console.error('Image src preview:', imgSrc.substring(0, 100));
      setImageError('Falha ao processar imagem');
      setImageProcessing(false);
    };

    img.onload = () => {
      console.log('✅ Image loaded successfully!');
      console.log('📐 Image dimensions:', img.width, 'x', img.height);
      try {
        // Instagram Stories format: 9:16 ratio (1080x1920)
        const targetWidth = 1080;
        const targetHeight = 1920;
        
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        
        // Calculate scaling to cover the canvas while maintaining aspect ratio
        const scale = Math.max(targetWidth / img.width, targetHeight / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        
        // Center the image
        const x = (targetWidth - scaledWidth) / 2;
        const y = (targetHeight - scaledHeight) / 2;
        
        // Draw background gradient
        const gradient = ctx.createLinearGradient(0, 0, 0, targetHeight);
        gradient.addColorStop(0, '#f8f9fa');
        gradient.addColorStop(1, '#e9ecef');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        
        // Draw original image (centered and scaled)
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
        
        // ===== CHRISTMAS DECORATIONS =====
        
        // Top corners - Stars
        drawStar(ctx, 80, 80, 5, 40, 18, '#FFD700');
        drawStar(ctx, targetWidth - 80, 80, 5, 35, 16, '#FFD700');
        
        // Top area - Snowflakes
        drawSnowflake(ctx, 200, 150, 25, 'rgba(255, 255, 255, 0.9)');
        drawSnowflake(ctx, targetWidth - 200, 150, 25, 'rgba(255, 255, 255, 0.9)');
        drawSnowflake(ctx, targetWidth / 2, 120, 30, 'rgba(255, 255, 255, 0.9)');
        
        // Side ornaments
        drawOrnament(ctx, 60, 400, 35, 'rgba(220, 38, 38, 1)');
        drawOrnament(ctx, targetWidth - 60, 450, 30, 'rgba(16, 185, 129, 1)');
        drawOrnament(ctx, 70, 700, 32, 'rgba(234, 179, 8, 1)');
        drawOrnament(ctx, targetWidth - 70, 750, 28, 'rgba(59, 130, 246, 1)');
        
        // Pine branches in corners
        drawPineBranch(ctx, 40, 250, 100, Math.PI / 6);
        drawPineBranch(ctx, targetWidth - 40, 280, 100, Math.PI - Math.PI / 6);
        
        // Bottom corners - Bows and Candy Canes
        drawBow(ctx, 100, targetHeight - 200, 40);
        drawBow(ctx, targetWidth - 100, targetHeight - 220, 35);
        drawCandyCane(ctx, 80, targetHeight - 400, 120);
        drawCandyCane(ctx, targetWidth - 60, targetHeight - 380, 100);
        
        // Christmas lights along top and sides
        drawChristmasLights(ctx, 150, 60, targetWidth - 150, 60, 8);
        drawChristmasLights(ctx, 40, 300, 40, 600, 5);
        drawChristmasLights(ctx, targetWidth - 40, 320, targetWidth - 40, 620, 5);
        
        // More snowflakes scattered
        drawSnowflake(ctx, 150, 500, 20, 'rgba(255, 255, 255, 0.8)');
        drawSnowflake(ctx, targetWidth - 150, 550, 22, 'rgba(255, 255, 255, 0.8)');
        drawSnowflake(ctx, targetWidth / 2, 900, 25, 'rgba(255, 255, 255, 0.85)');
        drawSnowflake(ctx, 180, 1100, 18, 'rgba(255, 255, 255, 0.75)');
        drawSnowflake(ctx, targetWidth - 180, 1150, 20, 'rgba(255, 255, 255, 0.8)');
        
        // ===== SCORE BADGE (INCREASED SIZE) =====
        
        // Dynamic badge color based on score
        let badgeColor: string;
        if (treeScore >= 9) badgeColor = '#10b981';
        else if (treeScore >= 7) badgeColor = '#eab308';
        else if (treeScore >= 5) badgeColor = '#f97316';
        else badgeColor = '#dc2626';
        
        // Score label translations
        const scoreLabels: Record<string, string> = {
          en: 'Rate',
          es: 'Puntuación',
          pt: 'Nota',
        };
        const scoreLabel = scoreLabels[currentLanguage] || scoreLabels.en;
        
        // Draw enhanced score badge in top right (BIGGER)
        const badgeSize = 180; // Increased from 140
        const badgeX = targetWidth - badgeSize - 40;
        const badgeY = 60; // Increased from 40 to make space for label
        
        // Badge shadow
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 20;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 4;
        
        // Badge background
        ctx.fillStyle = badgeColor;
        ctx.beginPath();
        ctx.arc(badgeX + badgeSize / 2, badgeY + badgeSize / 2, badgeSize / 2, 0, Math.PI * 2);
        ctx.fill();
        
        // Badge border
        ctx.shadowColor = 'transparent';
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.arc(badgeX + badgeSize / 2, badgeY + badgeSize / 2, badgeSize / 2 - 2, 0, Math.PI * 2);
        ctx.stroke();
        
        // Draw score label above badge
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 8;
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 28px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(scoreLabel, badgeX + badgeSize / 2, badgeY - 10);
        
        // Badge text (BIGGER FONTS)
        ctx.shadowColor = 'transparent';
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 72px Arial'; // Increased from 56px
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${treeScore}`, badgeX + badgeSize / 2, badgeY + badgeSize / 2 - 10);
        
        ctx.font = 'bold 32px Arial'; // Increased from 24px
        ctx.fillText('/10', badgeX + badgeSize / 2, badgeY + badgeSize / 2 + 36);
        
        // ===== LINK WITH BACKGROUND BANNER =====
        
        // Reset shadows for link banner
        ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
        ctx.shadowBlur = 15;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 3;
        
        // Draw background banner for link
        const linkY = targetHeight - 60;
        const bannerHeight = 70;
        const bannerWidth = 500;
        const bannerX = (targetWidth - bannerWidth) / 2;
        const bannerY = linkY - bannerHeight / 2;
        
        // Banner with Christmas gradient
        const linkGradient = ctx.createLinearGradient(bannerX, bannerY, bannerX, bannerY + bannerHeight);
        linkGradient.addColorStop(0, 'rgba(45, 122, 79, 0.95)'); // Christmas green
        linkGradient.addColorStop(1, 'rgba(22, 101, 52, 0.95)'); // Darker green
        
        ctx.fillStyle = linkGradient;
        ctx.beginPath();
        ctx.roundRect(bannerX, bannerY, bannerWidth, bannerHeight, 15);
        ctx.fill();
        
        // Banner border
        ctx.strokeStyle = 'rgba(212, 175, 55, 0.8)'; // Gold border
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.roundRect(bannerX, bannerY, bannerWidth, bannerHeight, 15);
        ctx.stroke();
        
        // Dynamic URL based on language
        const shareUrl = currentLanguage === 'pt' ? 'arvorenota10.com.br' : 'treerated10.com';
        
        // Link text
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 8;
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 36px Arial'; // Increased from 32px
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(shareUrl, targetWidth / 2, linkY);
        
        // Export as high-quality JPEG
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
        console.log('✅ Canvas converted to base64, length:', dataUrl.length);
        setEditedImage(dataUrl);
        setImageProcessing(false);
      } catch (error) {
        console.error('❌ Canvas rendering error:', error);
        setImageError('Erro ao criar preview');
        setImageProcessing(false);
      }
    };
    
    console.log('🔄 Setting img.src...');
    img.src = imgSrc;
  };

  const handleShare = async () => {
    if (!editedImage) {
      toast.error(t('result.waitProcessing'));
      return;
    }

    try {
      toast.loading(t('result.preparingImage'), { id: 'share-loading' });

      // Convert base64 to blob
      const blob = await (await fetch(editedImage)).blob();
      const file = new File([blob], 'my-christmas-tree.jpg', { type: 'image/jpeg' });

      // Check if Web Share API is available
      if ('share' in navigator) {
        try {
          await navigator.share({
            files: [file],
            title: t('result.myTree'),
            text: t('result.shareText', { score }),
          });
          toast.success(t('result.sharedSuccess'), { id: 'share-loading' });
          return;
        } catch (shareError: any) {
          // User cancelled
          if (shareError.name === 'AbortError') {
            toast.dismiss('share-loading');
            return;
          }
          // Web Share API doesn't support files, fallback to download
          console.log('Share with files failed, falling back to download:', shareError);
        }
      }

      // Fallback: Direct download
      const a = document.createElement('a');
      a.href = editedImage;
      a.download = 'my-christmas-tree.jpg';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(t('result.imageDownloaded'), { id: 'share-loading' });
    } catch (error: any) {
      console.error('Share error:', error);
      toast.error(t('result.shareError'), { id: 'share-loading' });
    }
  };

  const handleUpgrade = async () => {
    if (!user) {
      toast.error(t('result.loginError'));
      navigate('/auth');
      return;
    }

    try {
      setUpgrading(true);
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { currency },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(t('result.upgradeError'));
    } finally {
      setUpgrading(false);
    }
  };

  if (loading && !errorState) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Sparkles className="h-16 w-16 mx-auto mb-4 text-gold animate-pulse" />
          <p className="text-xl text-foreground">{t('result.loading')}</p>
        </div>
      </div>
    );
  }

  // Show error state with retry option
  if (errorState && !score) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-destructive">
          <CardContent className="p-6 text-center space-y-4">
            <p className="text-destructive text-lg font-medium">{errorState}</p>
            <p className="text-muted-foreground text-sm">
              {t('result.errorDescription', 'Ocorreu um erro ao avaliar sua árvore. Por favor, tente novamente.')}
            </p>
            <div className="flex gap-2 justify-center pt-2">
              <Button onClick={() => evaluateTree(originalImage || imageFromState)}>
                Tentar Novamente
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>
                Voltar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-snow to-background p-4 pb-24">
      <canvas ref={canvasRef} style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }} />
      
      <div className="max-w-2xl mx-auto pt-8 space-y-6 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('result.back')}
          </Button>
          
          {!isFreeTrial && (
            <PremiumCreditsDisplay 
              hasPremium={hasPremium}
              creditsRemaining={creditsRemaining}
              loading={creditsLoading}
            />
          )}
        </div>

        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-secondary mb-2 animate-scale-in">
            {score}/10
          </h1>
          <p className="text-muted-foreground">{t('result.yourScore')}</p>
        </div>

        {score && (
          <div className="space-y-3">
            {imageProcessing && (
              <Card className="overflow-hidden shadow-pine mx-auto max-w-sm">
                <CardContent className="p-0">
                  <div className="aspect-[9/16] bg-gradient-to-b from-muted/20 to-muted/40 rounded-lg overflow-hidden flex items-center justify-center">
                    <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            )}

            {imageError && (
              <Card className="overflow-hidden shadow-pine mx-auto max-w-sm border-destructive">
                <CardContent className="p-6 text-center">
                  <p className="text-destructive">{imageError}</p>
                </CardContent>
              </Card>
            )}

            {editedImage && !imageProcessing && (
              <Card className="overflow-hidden shadow-pine mx-auto max-w-sm">
                <CardContent className="p-0">
                  <div className="aspect-[9/16] bg-gradient-to-b from-muted/20 to-muted/40 rounded-lg overflow-hidden">
                    <img
                      src={editedImage}
                      alt="Rated tree"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

          </div>
        )}

        {isFreeTrial ? (
          // FREE TRIAL MODE
          <>
            <Card className="shadow-lg bg-gradient-to-br from-gold/10 to-secondary/10 border-2 border-gold/30">
              <CardContent className="p-8 text-center space-y-4">
                <Sparkles className="h-16 w-16 mx-auto text-gold animate-pulse" />
                <h2 className="text-2xl font-bold text-foreground">
                  {t('result.wantFullAnalysis')}
                </h2>
                <p className="text-muted-foreground text-lg">
                  {t('result.loginPrompt')}
                </p>
                <Button 
                  onClick={() => {
                    endFreeTrial();
                    navigate('/auth');
                  }}
                  size="lg"
                  className="w-full bg-gradient-to-r from-gold to-gold-light hover:opacity-90 text-pine font-bold text-lg"
                >
                  {t('result.evaluateAnother')}
                </Button>
              </CardContent>
            </Card>

            <Button
              onClick={handleShare}
              size="lg"
              className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-festive"
            >
              <Share2 className="mr-2 h-5 w-5" />
              {t('result.shareInstagram')}
            </Button>
          </>
        ) : (
          // LOGGED IN USER MODE
          <>
            <Card className="shadow-lg">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-foreground mb-3">
                  {t('result.description')}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {description}
                </p>
              </CardContent>
            </Card>

            {premiumInsights ? (
              <PremiumInsights insights={premiumInsights} />
            ) : !hasPremium ? (
              <Card className="shadow-lg bg-gradient-to-br from-primary/5 to-secondary/5 border-2 border-gold/20">
                <CardContent className="p-6 text-center">
                  <Sparkles className="h-12 w-12 mx-auto mb-4 text-gold" />
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    {t('result.insights')}
                  </h2>
                  <p className="text-muted-foreground mb-4">
                    Compre 77 avaliações premium e desbloqueie insights detalhados!
                  </p>
                  <div className="text-sm text-muted-foreground mb-4">
                    {CURRENCY_SYMBOLS[currency]}{PRICE_AMOUNTS[currency].toFixed(2)} {currency} - 77 avaliações
                  </div>
                  <Button 
                    onClick={handleUpgrade}
                    disabled={upgrading}
                    className="bg-gradient-to-r from-gold to-gold-light hover:opacity-90 text-pine font-semibold"
                  >
                    {upgrading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Comprar 77 Avaliações Premium'
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : null}

            <Button
              onClick={handleShare}
              size="lg"
              className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-festive"
            >
              <Share2 className="mr-2 h-5 w-5" />
              {t('result.shareButton')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default Result;
